/**
 * Input validation and sanitization utilities
 * Provides comprehensive validation for user inputs
 */

/**
 * Sanitize string input to prevent XSS attacks
 * @param {string} input - Input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeString = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .substring(0, 1000); // Limit length
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {Object} - Validation result
 */
export const validateEmail = (email) => {
  const sanitized = sanitizeString(email);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!sanitized) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (!emailRegex.test(sanitized)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  if (sanitized.length > 254) {
    return { isValid: false, error: 'Email address is too long' };
  }
  
  return { isValid: true, value: sanitized.toLowerCase() };
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result
 */
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters long' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long' };
  }
  
  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }
  
  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }
  
  // Check for at least one number
  if (!/\d/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }
  
  // Check for at least one special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one special character' };
  }
  
  return { isValid: true, value: password };
};

/**
 * Validate name (first name, last name)
 * @param {string} name - Name to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} - Validation result
 */
export const validateName = (name, fieldName = 'Name') => {
  const sanitized = sanitizeString(name);
  
  if (!sanitized) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  if (sanitized.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters long` };
  }
  
  if (sanitized.length > 50) {
    return { isValid: false, error: `${fieldName} is too long` };
  }
  
  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  if (!/^[a-zA-Z\s\-']+$/.test(sanitized)) {
    return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }
  
  return { isValid: true, value: sanitized };
};

/** Display labels only (signup/settings copy); stored values remain male/female. */
export const COMPETITION_CATEGORY_LABELS = { male: "Men's", female: "Women's" };

/**
 * Validate competition category stored as players.gender (male | female).
 * @param {string} gender - Category value from form
 */
export const validateGender = (gender) => {
  const validGenders = ['male', 'female'];

  if (!gender) {
    return { isValid: false, error: 'Competition category is required' };
  }

  if (!validGenders.includes(gender)) {
    return { isValid: false, error: "Please choose Men's or Women's for competition category" };
  }
  
  return { isValid: true, value: gender };
};

/**
 * Validate club name
 * @param {string} name - Club name to validate
 * @returns {Object} - Validation result
 */
export const validateClubName = (name) => {
  const sanitized = sanitizeString(name);
  
  if (!sanitized) {
    return { isValid: false, error: 'Club name is required' };
  }
  
  if (sanitized.length < 3) {
    return { isValid: false, error: 'Club name must be at least 3 characters long' };
  }
  
  if (sanitized.length > 100) {
    return { isValid: false, error: 'Club name is too long' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validate session title
 * @param {string} title - Session title to validate
 * @returns {Object} - Validation result
 */
export const validateSessionTitle = (title) => {
  const sanitized = sanitizeString(title);
  
  if (!sanitized) {
    return { isValid: false, error: 'Session title is required' };
  }
  
  if (sanitized.length < 3) {
    return { isValid: false, error: 'Session title must be at least 3 characters long' };
  }
  
  if (sanitized.length > 200) {
    return { isValid: false, error: 'Session title is too long' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validate numeric input
 * @param {string|number} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export const validateNumber = (value, options = {}) => {
  const { min, max, required = true, fieldName = 'Number' } = options;
  
  if (!value && !required) {
    return { isValid: true, value: null };
  }
  
  if (!value && required) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const numValue = Number(value);
  
  if (isNaN(numValue)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }
  
  if (min !== undefined && numValue < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min}` };
  }
  
  if (max !== undefined && numValue > max) {
    return { isValid: false, error: `${fieldName} must be no more than ${max}` };
  }
  
  return { isValid: true, value: numValue };
};

/**
 * Validate date input
 * @param {string|Date} date - Date to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export const validateDate = (date, options = {}) => {
  const { required = true, fieldName = 'Date', minDate, maxDate } = options;
  
  if (!date && !required) {
    return { isValid: true, value: null };
  }
  
  if (!date && required) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const dateValue = new Date(date);
  
  if (isNaN(dateValue.getTime())) {
    return { isValid: false, error: `${fieldName} must be a valid date` };
  }
  
  if (minDate && dateValue < new Date(minDate)) {
    return { isValid: false, error: `${fieldName} must be after ${new Date(minDate).toLocaleDateString()}` };
  }
  
  if (maxDate && dateValue > new Date(maxDate)) {
    return { isValid: false, error: `${fieldName} must be before ${new Date(maxDate).toLocaleDateString()}` };
  }
  
  return { isValid: true, value: dateValue };
};

/**
 * Validate form data for user registration
 * @param {Object} formData - Form data to validate
 * @returns {Object} - Validation result
 */
export const validateRegistrationForm = (formData) => {
  const errors = {};
  const validatedData = {};
  
  // Validate email
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  } else {
    validatedData.email = emailValidation.value;
  }
  
  // Validate password
  const passwordValidation = validatePassword(formData.password);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  } else {
    validatedData.password = passwordValidation.value;
  }
  
  // Validate first name
  const firstNameValidation = validateName(formData.first_name, 'First name');
  if (!firstNameValidation.isValid) {
    errors.first_name = firstNameValidation.error;
  } else {
    validatedData.first_name = firstNameValidation.value;
  }
  
  // Validate last name
  const lastNameValidation = validateName(formData.last_name, 'Last name');
  if (!lastNameValidation.isValid) {
    errors.last_name = lastNameValidation.error;
  } else {
    validatedData.last_name = lastNameValidation.value;
  }
  
  // Validate gender
  const genderValidation = validateGender(formData.gender);
  if (!genderValidation.isValid) {
    errors.gender = genderValidation.error;
  } else {
    validatedData.gender = genderValidation.value;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    data: validatedData
  };
};

/**
 * Validate form data for user login
 * @param {Object} formData - Form data to validate
 * @returns {Object} - Validation result
 */
export const validateLoginForm = (formData) => {
  const errors = {};
  const validatedData = {};
  
  // Validate email
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  } else {
    validatedData.email = emailValidation.value;
  }
  
  // Validate password (basic validation for login)
  if (!formData.password) {
    errors.password = 'Password is required';
  } else {
    validatedData.password = formData.password;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    data: validatedData
  };
};
